<template>
  <DialogBase
    :visible.sync="visible"
    :title="dialogTitle"
    size="lg"
    :confirm-loading="loading"
    confirm-text="Lưu"
    cancel-text="Đóng"
    :use-panel="true"
    panel-title="Nhận xét ngày"
    panel-subtitle="Tạo hoặc chỉnh sửa nhận xét cho học sinh theo ngày"
    panel-icon="el-icon-edit-outline"
    @confirm="handleSave"
    @cancel="handleClose"
  >
    <el-form
      ref="dataForm"
      :model="dataForm"
      :rules="formRules"
      label-position="left"
      label-width="140px"
      class="sk-u-mt-12"
    >
      <div class="sk-form-grid">
        <el-form-item label="Học sinh" prop="studentId">
          <el-select
            v-model="dataForm.studentId"
            placeholder="Chọn học sinh"
            filterable
            style="width: 100%"
            disabled
            @change="handleStudentChange"
          >
            <el-option
              v-for="item in studentList"
              :key="item.studentId"
              :label="item.studentName"
              :value="item.studentId"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Ngày" prop="date">
          <el-date-picker
            v-model="dataForm.date"
            type="date"
            value-format="yyyy-MM-dd"
            format="dd/MM/yyyy"
            placeholder="Chọn ngày"
            style="width: 100%"
            disabled
          />
        </el-form-item>
      </div>

      <!-- Items theo danh mục -->
      <div class="evl-items-section">
        <div
          v-for="(item, index) in dataForm.items"
          :key="index"
          class="evl-item-card"
        >
          <div class="evl-item-card__header">
            <el-select
              v-model="item.categoryId"
              placeholder="Chọn danh mục"
              style="width: 220px"
              :disabled="isEditMode && item._isExisting"
              @change="handleItemCategoryChange(index)"
            >
              <el-option
                v-for="cat in getAvailableCategories(index)"
                :key="String(cat.id)"
                :label="cat.name"
                :value="String(cat.id)"
              />
            </el-select>
          </div>

          <!-- Template Picker cho item này -->
          <div v-if="item._templates && item._templates.length > 0" class="evl-item-templates">
            <div class="template-picker">
              <div
                v-for="tpl in item._templates"
                :key="tpl.id"
                class="template-item"
                @click="selectTemplateForItem(index, tpl)"
              >
                <i class="el-icon-document" />
                <span>{{ tpl.content }}</span>
              </div>
            </div>
          </div>
          <div v-else-if="item._templateLoading" class="evl-item-templates">
            <span class="template-loading-hint"><i class="el-icon-loading" /> Đang tải mẫu...</span>
          </div>

          <el-input
            v-model="item.content"
            type="textarea"
            :rows="3"
            placeholder="Nhập nội dung nhận xét hoặc chọn mẫu ở trên..."
            maxlength="2000"
            show-word-limit
          />
        </div>

        <!-- Nút thêm danh mục -->
        <el-button
          v-if="availableCategoriesForAdd.length > 0"
          type="primary"
          plain
          icon="el-icon-plus"
          size="small"
          class="evl-add-item-btn"
          @click="addItem"
        >
          Thêm danh mục
        </el-button>
      </div>

      <!-- File/ảnh đính kèm (Ở DƯỚI CÙNG SAU CÁC NHẬN XÉT) -->
      <div class="evl-upload-section">
        <div class="evl-upload-section__header">
          <span class="evl-upload-title">
            <i class="el-icon-paperclip" /> File & hình ảnh đính kèm
          </span>
          <span class="evl-upload-subtitle">Tối đa 3 file/nhận xét (Ảnh, Word, Excel hoặc PDF)</span>
        </div>
        <el-upload
          drag
          action="#"
          :auto-upload="false"
          :file-list="fileList"
          :limit="3"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          multiple
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :on-exceed="handleFileExceed"
          class="evl-drag-uploader"
        >
          <i class="el-icon-upload" />
          <div class="el-upload__text">
            Kéo thả file/ảnh vào đây hoặc <em>nhấn để chọn file</em>
          </div>
        </el-upload>
      </div>
    </el-form>
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import EvaluationCategoryService from "@/modules/evaluation/services/EvaluationCategoryService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { EVALUATION_FILE_MAX_COUNT, isEvaluationFileAllowed } from "@/modules/evaluation/utils/evaluationFile";
import moment from "moment";

export default {
  name: "EvaluationDailyFormDialog",
  components: { DialogBase },
  props: {
    classId: { type: [Number, String], default: "" },
    date: { type: String, default: "" },
    studentList: { type: Array, default: () => [] },
  },
  data() {
    return {
      visible: false,
      loading: false,
      isEditMode: false,
      editUid: null,
      dataForm: {
        studentId: "",
        studentName: "",
        date: moment().format("YYYY-MM-DD"),
        items: [{ categoryId: "", categoryName: "", content: "", _templates: [], _templateLoading: false }],
      },
      formRules: {
        studentId: [{ required: true, message: "Vui lòng chọn học sinh", trigger: "change" }],
        date: [{ required: true, message: "Vui lòng chọn ngày", trigger: "change" }],
      },
      categoryList: [],
      originalItems: [],
      originalFiles: [],
      fileList: [],
    };
  },
  computed: {
    dialogTitle() {
      return this.isEditMode ? "Chỉnh sửa nhận xét ngày" : "Tạo nhận xét ngày";
    },
    schoolId() {
      return this.$store.state.auth.user.schoolId;
    },
    teacherId() {
      return this.$store.state.auth.user.id;
    },
    teacherName() {
      return this.$store.state.auth.user.fullName;
    },
    // Danh mục chưa được chọn bởi bất kỳ item nào (dùng cho nút "Thêm danh mục")
    availableCategoriesForAdd() {
      const usedIds = this.dataForm.items.map(i => i.categoryId).filter(Boolean);
      return this.categoryList.filter(c => !usedIds.includes(String(c.id)));
    },
  },
  methods: {
    async openForCreate(student) {
      this.isEditMode = false;
      this.editUid = null;
      this._resetForm();
      if (student) {
        this.dataForm.studentId = student.studentId;
        this.dataForm.studentName = student.studentName;
      }
      this.dataForm.date = this.date || moment().format("YYYY-MM-DD");
      await this.fetchCategories();
      this.visible = true;
    },

    async openForEdit(rowData) {
      this.isEditMode = true;
      this.editUid = rowData.evaluation.uid;
      this._resetForm();
      this.dataForm.studentId = rowData.studentId;
      this.dataForm.studentName = rowData.studentName;
      this.dataForm.date = rowData.evaluation.date;

      // Load items từ evaluation
      if (rowData.evaluation.items && rowData.evaluation.items.length > 0) {
        this.dataForm.items = rowData.evaluation.items.map(item => ({
          categoryId: String(item.categoryId || ""),
          categoryName: item.categoryName,
          content: item.content,
          _isExisting: true,
          _templates: [],
          _templateLoading: false,
        }));
      } else {
        // Fallback: nếu dữ liệu cũ vẫn có category + content ở root
        this.dataForm.items = [{
          categoryId: String(rowData.evaluation.categoryId || ""),
          categoryName: rowData.evaluation.category || rowData.evaluation.categoryName || "",
          content: rowData.evaluation.content || "",
          _isExisting: true,
          _templates: [],
          _templateLoading: false,
        }];
      }
      this.originalItems = this.normalizeItems(this.dataForm.items);
      this.fileList = (rowData.evaluation.files || []).map((file, index) => ({
        uid: `existing-${index}`,
        name: String(file).split("/").pop(),
        existing: true,
        objectKey: file,
        status: "success",
      }));
      this.originalFiles = this.getFileState();

      await this.fetchCategories();
      this.visible = true;
    },

    async fetchCategories() {
      try {
        const res = await EvaluationCategoryService.getCategories();
        this.categoryList = res.data.data || [];

        if (!this.isEditMode) {
          // Khi tạo mới, hiển thị sẵn một ô nhập cho mọi danh mục.
          // Người dùng không cần phải bấm "Thêm danh mục" nhiều lần.
          this.dataForm.items = this.categoryList.map(category => ({
            categoryId: String(category.id),
            categoryName: category.name,
            content: "",
            _isExisting: false,
            _templates: [],
            _templateLoading: false,
          }));

          this.dataForm.items.forEach((item, index) => {
            this.fetchTemplatesForItem(index, item.categoryId);
          });
        } else {
          // Khi chỉnh sửa, vẫn hiển thị đủ danh mục và ghép nội dung đã lưu vào đúng danh mục.
          const existingItems = this.dataForm.items;
          const existingByCategory = existingItems.reduce((result, item) => {
            if (item.categoryId) result[item.categoryId] = item;
            return result;
          }, {});

          this.dataForm.items = this.categoryList.map(category => {
            const existingItem = existingByCategory[String(category.id)];
            return existingItem || {
              categoryId: String(category.id),
              categoryName: category.name,
              content: "",
              _isExisting: false,
              _templates: [],
              _templateLoading: false,
            };
          });

          // Giữ lại dữ liệu cũ thuộc danh mục đã bị xoá/không còn trong danh sách cấu hình.
          const categoryIds = new Set(this.categoryList.map(category => String(category.id)));
          this.dataForm.items.push(
            ...existingItems.filter(item => item.categoryId && !categoryIds.has(String(item.categoryId)))
          );

          // Load templates cho toàn bộ các danh mục đang hiển thị.
          this.dataForm.items.forEach((item, idx) => {
            if (item.categoryId) {
              this.fetchTemplatesForItem(idx, item.categoryId);
            }
          });
        }
      } catch {
        this.categoryList = [];
      }
    },

    async fetchTemplatesForItem(index, categoryId) {
      if (!categoryId) {
        this.$set(this.dataForm.items[index], "_templates", []);
        return;
      }
      this.$set(this.dataForm.items[index], "_templateLoading", true);
      try {
        const res = await EvaluationCategoryService.getTemplates(categoryId);
        this.$set(this.dataForm.items[index], "_templates", res.data.data || []);
      } catch {
        this.$set(this.dataForm.items[index], "_templates", []);
      } finally {
        this.$set(this.dataForm.items[index], "_templateLoading", false);
      }
    },

    // Danh mục available cho 1 item cụ thể (bao gồm danh mục hiện tại + các cái chưa dùng)
    getAvailableCategories(index) {
      const currentId = this.dataForm.items[index].categoryId;
      const usedIds = this.dataForm.items
        .map((item, idx) => idx !== index ? item.categoryId : null)
        .filter(Boolean);
      return this.categoryList.filter(c => !usedIds.includes(String(c.id)) || String(c.id) === currentId);
    },

    handleItemCategoryChange(index) {
      const item = this.dataForm.items[index];
      const cat = this.categoryList.find(c => String(c.id) === item.categoryId);
      item.categoryName = cat ? cat.name : "";
      this.fetchTemplatesForItem(index, item.categoryId);
    },

    selectTemplateForItem(index, tpl) {
      const item = this.dataForm.items[index];
      if (item.content && item.content.trim()) {
        item.content = item.content.trimEnd() + "\n" + tpl.content;
      } else {
        item.content = tpl.content;
      }
    },

    addItem() {
      this.dataForm.items.push({
        categoryId: "",
        categoryName: "",
        content: "",
        _isExisting: false,
        _templates: [],
        _templateLoading: false,
      });
    },

    removeItem(index) {
      this.dataForm.items.splice(index, 1);
    },

    async handleSave() {
      const valid = await this.$refs.dataForm.validate().catch(() => false);
      if (!valid) return;

      // Chỉ giữ lại danh mục có mã và nội dung thực tế; danh mục trống không gửi lên BE.
      const items = this.dataForm.items
        .filter(item => item.categoryId && typeof item.content === "string" && item.content.trim().length > 0)
        .map(item => ({
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          content: item.content.trim(),
        }));

      // Không gọi API nếu người dùng chưa nhập nội dung ở bất kỳ danh mục nào.
      if (items.length === 0) {
        NotifyMessage.leftCornerNotificationError("Vui lòng nhập ít nhất 1 danh mục có nội dung nhận xét!");
        return;
      }

      // Check trùng categoryId
      const catIds = items.map(item => item.categoryId);
      if (new Set(catIds).size !== catIds.length) {
        NotifyMessage.leftCornerNotificationError("Không được chọn trùng danh mục!");
        return;
      }

      if (this.isEditMode
        && JSON.stringify(this.normalizeItems(items)) === JSON.stringify(this.originalItems)
        && JSON.stringify(this.getFileState()) === JSON.stringify(this.originalFiles)) {
        NotifyMessage.leftCornerNotificationError("Chưa có thay đổi để lưu!");
        return;
      }

      this.loading = true;
      try {
        if (this.isEditMode) {
          const payload = {
            classId: this.classId,
            studentId: this.dataForm.studentId,
            date: this.dataForm.date,
            uid: this.editUid,
            items: items,
            files: this.getFiles(),
            existingFiles: this.getExistingFiles(),
          };
          await EvaluationService.updateDaily(payload);
          NotifyMessage.leftCornerNotificationSuccess("Cập nhật nhận xét thành công!");
        } else {
          const student = this.studentList.find(s => s.studentId === this.dataForm.studentId);
          const payload = {
            classId: this.classId,
            studentId: this.dataForm.studentId,
            studentName: student ? student.studentName : this.dataForm.studentName,
            date: this.dataForm.date,
            items: items,
            files: this.getFiles(),
          };
          await EvaluationService.createDaily(payload);
          NotifyMessage.leftCornerNotificationSuccess("Tạo nhận xét thành công!");
        }
        this.$emit("saved");
        this.handleClose();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleClose() {
      this.$refs.dataForm?.resetFields();
      this.visible = false;
    },

    handleStudentChange(val) {
      const student = this.studentList.find(s => s.studentId === val);
      if (student) this.dataForm.studentName = student.studentName;
    },

    handleFileChange(file, fileList) {
      if (!isEvaluationFileAllowed(file.raw)) {
        NotifyMessage.leftCornerNotificationError("Chỉ được tải ảnh, file Word, Excel hoặc PDF!");
        this.fileList = fileList.filter(item => item.uid !== file.uid);
        return;
      }
      if (fileList.length > EVALUATION_FILE_MAX_COUNT) {
        NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
        this.fileList = fileList.slice(0, EVALUATION_FILE_MAX_COUNT);
        return;
      }
      this.fileList = fileList;
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleFileExceed() {
      NotifyMessage.leftCornerNotificationError("Mỗi nhận xét chỉ được đính kèm tối đa 3 file!");
    },

    getFiles() {
      return this.fileList.map(file => file.raw).filter(Boolean);
    },

    getExistingFiles() {
      return this.fileList.filter(file => file.existing).map(file => file.objectKey);
    },

    getFileState() {
      return this.fileList.map(file => file.existing
        ? `existing:${file.objectKey}`
        : `new:${file.name}:${file.size || file.raw?.size || 0}:${file.raw?.lastModified || 0}`
      ).sort();
    },

    _resetForm() {
      this.originalItems = [];
      this.originalFiles = [];
      this.fileList = [];
      this.dataForm = {
        studentId: "",
        studentName: "",
        date: moment().format("YYYY-MM-DD"),
        items: [{ categoryId: "", categoryName: "", content: "", _templates: [], _templateLoading: false }],
      };
      this.$nextTick(() => {
        this.$refs.dataForm?.clearValidate();
      });
    },

    normalizeItems(items) {
      return items
        .filter(item => item.categoryId && typeof item.content === "string" && item.content.trim().length > 0)
        .map(item => ({
          categoryId: String(item.categoryId),
          content: item.content.trim(),
        }))
        .sort((first, second) => first.categoryId.localeCompare(second.categoryId));
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

/* ─── Items Section ────────────────────────────── */
.evl-items-section {
  margin-top: 8px;
  max-height: 440px;
  overflow-y: auto;
  padding-right: 6px;
}

.evl-item-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  transition: border-color 0.15s;

  &:hover {
    border-color: #cbd5e1;
  }
}

.evl-item-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.evl-item-remove {
  color: #ef4444;
  font-size: 18px;
  padding: 4px;
  &:hover { color: #dc2626; }
}

.evl-item-templates {
  margin-bottom: 10px;
}

.evl-add-item-btn {
  margin-top: 4px;
  border-radius: 10px;
}

/* ─── Template Picker ──────────────────────────── */
.template-picker {
  max-height: 120px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px;
  background: #ffffff;
}

.template-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  border: 1px solid transparent;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;

  i {
    margin-top: 2px;
    flex-shrink: 0;
    color: #94a3b8;
  }

  &:hover {
    background: #eff6ff;
    border-color: #dbeafe;
  }
}

.template-loading-hint {
  color: #64748b;
  font-size: 13px;
  i { margin-right: 4px; }
}

/* ─── Modern Drag Dropzone ───────────────────────── */
.evl-upload-section {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px dashed #e2e8f0;
}

.evl-upload-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.evl-upload-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #2563eb;
    font-size: 15px;
  }
}

.evl-upload-subtitle {
  font-size: 12px;
  color: #94a3b8;
}

.evl-drag-uploader {
  width: 100%;

  /deep/ .el-upload {
    width: 100%;
  }

  /deep/ .el-upload-dragger {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    background: #f8fafc;
    transition: all 0.2s ease;

    &:hover {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .el-icon-upload {
      font-size: 32px;
      color: #3b82f6;
      margin: 0 0 2px;
      line-height: 1;
    }

    .el-upload__text {
      font-size: 13px;
      color: #64748b;
      line-height: 1.4;

      em {
        color: #2563eb;
        font-weight: 600;
        font-style: normal;
      }
    }
  }

  /deep/ .el-upload-list {
    margin-top: 8px;

    .el-upload-list__item {
      border-radius: 8px;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      margin-top: 6px;
      transition: all 0.15s ease;

      &:hover {
        background: #e2e8f0;
      }
    }
  }
}
</style>
